import React, { useState, useEffect } from "react";
import useTextToSpeech from "../../hooks/useTextToSpeech";
import {
  AudioPlayIcon,
  ChevronDown,
  ChevronUp,
  FileText,
  FileTextFill,
  PauseFill,
  PlayFill,
  StopFill,
} from "../svgs/Svgs";
import "./AudioPlayer.css";

const AudioPlayer = ({
  content,
  title,
  durationEstimate,
  voiceStyle = "educational",
  language = "en",
  className = "",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [showContentPreview, setShowContentPreview] = useState(false);

  const {
    isPlaying,
    isPaused,
    progress,
    duration,
    currentTime,
    isLoading,
    isSupported,
    speak,
    stop,
    togglePlayPause,
    getVoices,
  } = useTextToSpeech();

  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState("");

  useEffect(() => {
    // Load voices when component mounts
    const loadVoices = () => {
      const availableVoices = getVoices();
      setVoices(availableVoices);

      // Set default voice based on language and style
      const defaultVoice = availableVoices.find(
        (voice) =>
          voice.lang.startsWith(language) &&
          (voiceStyle === "educational"
            ? voice.name.includes("Female") || voice.name.includes("female")
            : true)
      );

      if (defaultVoice) {
        setSelectedVoice(defaultVoice.name);
      }
    };

    loadVoices();

    // Some browsers load voices asynchronously
    if (window.speechSynthesis) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }

    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [getVoices, language, voiceStyle]);

  const handlePlay = () => {
    if (!content) return;

    const options = {
      rate: playbackRate,
      volume: volume,
      lang: language,
      voice: selectedVoice,
    };

    speak(content, options);
  };

  const handleStop = () => {
    stop();
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const estimatedDuration = durationEstimate || duration;

  if (!isSupported) {
    return (
      <div className={`audio-player-container ${className}`}>
        <div className="audio-player-error">
          <i className="bi bi-exclamation-triangle"></i>
          <span>Text-to-speech is not supported in your browser.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`audio-player-container ${className}`}>
      {/* Main Audio Player Card */}
      <div className="audio-player-card">
        {/* Header Section */}
        <div className="audio-header">
          <div className="audio-title-section">
            <h4 className="audio-title">{title || "Audio Summary"}</h4>
            <div className="audio-meta">
              <span className="duration">{formatTime(estimatedDuration)}</span>
              <span className="separator">•</span>
              <span className="voice-style">{voiceStyle}</span>
            </div>
          </div>

          <div className="audio-actions">
            <button
              className="btn-expand btn-icon"
              onClick={() => setIsExpanded(!isExpanded)}
              title={isExpanded ? "Collapse" : "Expand"}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              {isExpanded ? (
                <ChevronUp color="#f05203" />
              ) : (
                <ChevronDown color="#f05203" />
              )}
            </button>
          </div>
        </div>

        {/* Main Controls Section */}
        <div className="audio-controls-main">
          <div className="play-button-container">
            <button
              className={`play-button ${
                isPlaying ? "playing" : isPaused ? "paused" : ""
              }`}
              onClick={isPlaying || isPaused ? togglePlayPause : handlePlay}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="loading-spinner"></div>
              ) : isPlaying ? (
                <PauseFill />
              ) : isPaused ? (
                <PlayFill />
              ) : (
                <PlayFill />
              )}
            </button>
          </div>

          <div className="progress-section">
            <div className="time-display">
              <span className="current-time">{formatTime(currentTime)}</span>
              <span className="total-time">
                {formatTime(estimatedDuration)}
              </span>
            </div>

            <div className="progress-container">
              <div className="progress-bar-bg">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="secondary-controls">
            <button
              className={`btn-stop btn-icon ${
                !isPlaying && !isPaused ? "disabled" : ""
              }`}
              onClick={handleStop}
              title="Stop"
              aria-label="Stop"
              disabled={!isPlaying && !isPaused}
            >
              <StopFill color="#b91c1c" />
            </button>

            <button
              className="btn-content btn-icon"
              onClick={() => setShowContentPreview(!showContentPreview)}
              title={showContentPreview ? "Hide Content" : "Show Content"}
              aria-label={showContentPreview ? "Hide Content" : "Show Content"}
            >
              {showContentPreview ? (
                <FileTextFill color="#82bd00" />
              ) : (
                <FileText color="#82bd00" />
              )}
            </button>
          </div>
        </div>

        {/* Advanced Controls */}
        {isExpanded && (
          <div className="audio-controls-advanced">
            <div className="controls-grid">
              <div className="control-group">
                <label className="control-label">Voice</label>
                <select
                  className="control-select"
                  value={selectedVoice}
                  onChange={(e) => setSelectedVoice(e.target.value)}
                >
                  <option value="">Default Voice</option>
                  {voices
                    .filter((voice) => voice.lang.startsWith(language))
                    .map((voice, index) => (
                      <option key={index} value={voice.name}>
                        {voice.name} ({voice.lang})
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Content Preview */}
        {showContentPreview && content && (
          <div className="content-preview-section">
            <div className="content-header">
              <h6>Content Preview</h6>
              <button
                className="btn-close-preview"
                onClick={() => setShowContentPreview(false)}
              >
                <i className="bi bi-x"></i>
              </button>
            </div>
            <div className="content-text">{content}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioPlayer;
